import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useUrlParam } from '../../utils/useUrlParam';
import { useSearchParams } from 'react-router-dom';

import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { NitroAd } from '../../ads/NitroAd';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import { CommandersPane } from './panes/CommandersPane';
import { StaplesPane } from './panes/StaplesPane';
import { formatInt } from '../common/cardStats/formatters';
import { getAnalytics, logEvent } from 'firebase/analytics';
import './Meta.css';

// Tier caps are declared in one place so the legend, the tier-band
// headers, and any future copy stay in sync.
const TIER_CAPS = [
    { tier: 1, cap: '10.99', key: 'one', label: 'Tier 1' },
    { tier: 2, cap: '10.69', key: 'two', label: 'Tier 2' },
    { tier: 3, cap: '10.29', key: 'three', label: 'Tier 3' },
    { tier: 4, cap: '9.29', key: 'four', label: 'Outside', footer: 'cap' },
];

const TABS = [
    { id: 'commanders', label: 'Commanders' },
    { id: 'staples', label: 'Staples' },
];

export function Meta() {
    const isFetching = useSelector((s) => s?.meta?.isFetching);
    const metaBlob = useSelector((s) => s?.meta?.metaBlob);
    const [tab] = useUrlParam('tab', 'commanders');
    const [, setSearchParams] = useSearchParams();

    // Each pane owns its own per-pane filter keys (q, tier, type, category)
    // but they share the URL — switching tabs drops the outgoing pane's
    // state so the new tab opens fresh instead of inheriting unrelated
    // search/filter values from its sibling.
    const switchTab = (id) => {
        if (id === tab) return;
        setSearchParams(
            (prev) => {
                const sp = new URLSearchParams(prev);
                ['q', 'tier', 'type', 'category'].forEach((k) => sp.delete(k));
                if (id === 'commanders') sp.delete('tab');
                else sp.set('tab', id);
                return sp;
            },
            { replace: true }
        );
        logEvent(getAnalytics(), `meta_setTab_${id.toUpperCase()}`);
    };

    const totalCommanders =
        (metaBlob?.meta?.tiers?.one?.ids?.length || 0) +
        (metaBlob?.meta?.tiers?.two?.ids?.length || 0) +
        (metaBlob?.meta?.tiers?.three?.ids?.length || 0) +
        (metaBlob?.meta?.tiers?.four?.ids?.length || 0);

    const topSlots = (metaBlob?.eventCount || 0) * 16;

    return (
        <div className="metaPageRoot">
            <Helmet>
                <title>cEDH Meta</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href="https://www.commandersalt.com/meta" />
                <meta name="description" content="cEDH Meta Commanders and Staples" />
                <meta name="twitter:title" content="Commandersalt.com - cEDH Meta Commanders and Staples" />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content="Commandersalt.com - cEDH Meta Commanders and Staples" />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content="cEDH Meta Commanders and Staples" />
            </Helmet>

            <BreadCrumbs />
            {isFetching && (
                <GenericLoadingDialog
                    eyebrow="Snapshot"
                    title="Loading cEDH metadata"
                    caption="Pulling the latest top-16 reports"
                    subcaption="Crunching tournament data — hang tight."
                />
            )}

            <div className="metaHero panelGradient">
                <div className="metaHeroTopRow">
                    <div className="metaHeroTitleBlock">
                        <span className="metaHeroEyebrow">cEDH META SNAPSHOT</span>
                        <h1 className="metaHeroTitle">What&apos;s actually winning right now?</h1>
                        <p className="metaHeroSubtitle">
                            Built from the last three months of tournament reports on{' '}
                            <a
                                href="https://edhtop16.com"
                                target="_blank"
                                rel="noreferrer"
                                className="metaHeroLink"
                            >
                                edhtop16.com
                            </a>
                            . We tier every commander that actually shows up in top-16 slots and cap power
                            scores accordingly.
                        </p>
                    </div>

                    <div className="metaHeroMetrics">
                        <MetricBox label="Decks" value={formatInt(metaBlob?.parsedDeckCount)} hint="parsed" />
                        <MetricBox label="Events" value={formatInt(metaBlob?.eventCount)} hint={`${formatInt(topSlots)} top-16 slots`} />
                        <MetricBox label="Commanders" value={formatInt(totalCommanders)} hint="in the meta" />
                        <MetricBox label="Staples" value={formatInt(metaBlob?.staplesCount)} hint="tracked" />
                    </div>
                </div>

                <div className="metaTierCapsRow">
                    <span className="metaTierCapsLabel">POWER CAPS BY TIER</span>
                    <div className="metaTierCapsList">
                        {TIER_CAPS.map((t) => (
                            <div key={t.key} className={`metaTierCap metaTierCap--${t.key}`}>
                                <span className="metaTierCapName">{t.label}</span>
                                <span className="metaTierCapValue">{t.cap}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <NitroAd id="meta" />

            <nav className="metaTabBar" role="tablist" aria-label="Meta views">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        className={`metaTab${tab === t.id ? ' metaTab--active' : ''}`}
                        onClick={() => switchTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </nav>

            <div className="metaPanelHost">
                {tab === 'commanders' && <CommandersPane metaBlob={metaBlob} />}
                {tab === 'staples' && <StaplesPane metaBlob={metaBlob} />}
            </div>
        </div>
    );
}

function MetricBox({ label, value, hint }) {
    return (
        <div className="metaHeroMetric">
            <span className="metaHeroMetricLabel">{label}</span>
            <span className="metaHeroMetricValue">{value ?? '—'}</span>
            {hint && <span className="metaHeroMetricHint">{hint}</span>}
        </div>
    );
}
